<template>
    <div class="flex-root-column replacements-merc-list">
        <div class="flex-root-column editor-box full-height replacements-merc-list-table">
            <div class="editor-table replacements">
                <table class="vcs-table" ref="replacementTable">
                    <thead>
                        <tr>
                            <th
                                v-for="header in columns"
                                v-show="visibleColumns[header.id]"
                                :key="header.id"
                                class="vcs-table-header"
                                :class="(ownSortingColumn === header.id ? 'sorted' + (ownSortingDirection === 'asc' ? ' sorted-ascending' : ' sorted-descending' ) : '')"
                                :style="(header.width ? 'width: ' + header.width + 'px;' : '')"
                                @click="changeSorting(header.id)"
                            >
                                <div class="vcs-table-header-title">{{ $t(header.title) }}{{
                                        header.required ? ' *' : ''
                                    }}
                                </div>
                                <div
                                    v-show="header.filter"
                                    class="vcs-table-header-filter"
                                    :class="{active: activeFilterValues[header.id] && activeFilterValues[header.id].length}"
                                    @click.stop="showFilter(header.id)"
                                    :title="filterTitle(header.id)"
                                />
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-if="paginatedData.length === 0"
                            class="no-data">
                            <td :colspan="columns.length">-- {{ $t('no CEDEX configuration found') }} --<br/>
                                <div class="add-first-line" @click="addRow(-1)">{{ $t('add replacement') }}</div>
                            </td>
                        </tr>
                        <tr
                            v-for="(item, index) in paginatedData"
                            :key="index + '_' + JSON.stringify(item)"
                            :class="{selected: index === selectedRowIndex}"
                            @click="handleRowClick(index)"
                            @dblclick="handleRowClick(index, true)"
                        >
                            <td v-if="visibleColumns.surveyType">
                                <KeysToValuesDisplay
                                    :keys="item.surveyType"
                                    :values="surveyTypes"
                                />
                            </td>
                            <td v-if="visibleColumns.containerHeight">
                                {{ containerHeightsShort[item.containerHeight] }}
                            </td>
                            <td v-if="visibleColumns.serviceType">
                                <KeysToValuesDisplay
                                    :keys="item.serviceType"
                                    :values="serviceTypes"
                                />
                            </td>
                            <td v-if="visibleColumns.mode">{{ modeDisplay(item) }}</td>
                            <td v-if="visibleColumns.viewType">{{ viewTypeDisplay(item) }}</td>
                            <td v-if="visibleColumns.machineryManufacturer">
                                <KeysToValuesDisplay
                                    :keys="item.machineryManufacturer"
                                    :values="machineryManufacturers"
                                />
                            </td>
                            <td v-if="visibleColumns.machineryType">{{ item.machineryManufacturer && item.machineryManufacturer.length === 1 ? machineryTypes[item.machineryManufacturer[0]][item.machineryType] : '' }}</td>
                            <td v-if="visibleColumns.detailedPosition">{{ detailedPositions[item.detailedPosition] }}</td>
                            <td v-if="visibleColumns.componentCode">{{ item.componentCode }}{{ codeTexts && item.componentCode !== '' ? ' - ' + componentCodes[item.componentCode] : '' }}</td>
                            <td v-if="visibleColumns.componentTag">
                                <KeysToValuesDisplay
                                    :keys="item.componentTag"
                                    :values="componentTags"
                                />
                            </td>
                            <td v-if="visibleColumns.refrigerant">{{ refrigerants[item.refrigerant] }}</td>
                            <td v-if="visibleColumns.damageTag">
                                <KeysToValuesDisplay
                                    :keys="item.damageTag"
                                    :values="damageTags"
                                />
                            </td>
                            <td v-if="visibleColumns.repairCode">
                                <KeysToValuesDisplay
                                    :keys="item.repairCode"
                                    :values="repairCodes"
                                    :show-keys-as-value="true"
                                    :show-keys-and-value="codeTexts"
                                />
                            </td>
                            <td v-if="visibleColumns.repairTag">
                                <KeysToValuesDisplay
                                    :keys="item.repairTag"
                                    :values="repairTags"
                                />
                            </td>
                            <td v-if="visibleColumns.length"><LengthDisplay
                                    :type="item.lengthType"
                                    :from="item.lengthFrom"
                                    :to="item.lengthTo"
                            /></td>
                            <td v-if="visibleColumns.width"><LengthDisplay
                                :type="item.widthType"
                                :from="item.widthFrom"
                                :to="item.widthTo"
                            /></td>
                            <td v-if="visibleColumns.quantity"><LengthDisplay
                                :type="item.quantityType"
                                :from="item.quantityFrom"
                                :to="item.quantityTo"
                            /></td>
                            <td v-if="visibleColumns.perimeter"><LengthDisplay
                                v-if="item.lengthType && item.lengthFrom && item.widthType && item.widthFrom"
                                :type="item.perimeterType"
                                :from="item.perimeterFrom"
                                :to="item.perimeterTo"
                            /></td>
                            <td
                                v-if="visibleColumns.stsCode"
                                class="stsCode"
                                :title="stsCodeTitle(item)"
                            >{{ item.stsCode1 }} {{ item.stsCode2 }} {{ item.stsCode3 }} {{ item.stsCode4 }}</td>
                            <td v-if="visibleColumns.maxQuantity">{{ item.maxQuantity }}</td>
                            <td v-if="visibleColumns.wageHoursPerQuantity">{{ item.wageHoursPerQuantity }}</td>
                            <td v-if="visibleColumns.materialCostValue">{{ item.materialCostValue }}</td>
                            <td v-if="visibleColumns.materialCost" class="center">
                                <img class="boolean"
                                     :src="require('@/assets/icons/check_box_' + (item.materialCost === '1' ? '' : 'un') + 'checked.svg')"
                                     :alt="(item.materialCost ? 'active' : 'inactive' )"
                                >
                            </td>
                            <td v-if="visibleColumns.preparationTime" class="center">
                                <img class="boolean"
                                     :src="require('@/assets/icons/check_box_' + (item.preparationTime === '1' ? '' : 'un') + 'checked.svg')"
                                     :alt="(item.preparationTime ? 'active' : 'inactive' )"
                                >
                            </td>
                            <td v-if="visibleColumns.mappingDisabled" class="center">
                                <img class="boolean"
                                     :src="require('@/assets/icons/check_box_' + (item.mappingDisabled === '1' ? '' : 'un') + 'checked.svg')"
                                     :alt="(item.mappingDisabled ? 'active' : 'inactive' )"
                                >
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <Pagination
                :pageCount="pageCount"
                :page-size="pageSize"
                :visible-buttons="5"
                v-model="currentPage"
                @input="setScrollTop"
                @pageSizeChange="setPaginationChange"
            />
            <div v-show="loading" class="loading"></div>
        </div>

        <TableFilters
            key="merc"
            ref="listFilters"
            :filters="filters"
            @change="onFiltersChange"
        />
    </div>
</template>

<script>
import TableFilters from "../../Table/Filter/TableFilters.vue";
import Pagination from "../../Table/Pagination.vue";
import i18n from "../../../i18n";
import LengthDisplay from "./Display/LengthDisplay";
import KeysToValuesDisplay from "./Display/KeysToValuesDisplay";
import equipments from "../../../plugins/constants";
import replacementTrait from "../../../plugins/traits/replacement";

export default {
    name: "ReplacementsMercList",
    components: {KeysToValuesDisplay, LengthDisplay, Pagination, TableFilters},
    props: {
        // view data selection
        codeTexts: Boolean,
        equipment: String,
        // numeric index array with all columnData (0 => {id, title, visible, width, filter, required})
        columns:   Array,

        // dependency data
        data: Array,
        sortingColumn: {
            type: String,
            default: null
        },
        sortingDirection: {
            type: String,
            default: null
        },

        // codes and types
        surveyTypes:            Object,
        serviceTypes:           Object,
        containerHeights:       Object,
        machineryManufacturers: Object,
        machineryTypes:         Object,
        modes:                  Object,
        modeReplacements:       Array,
        viewTypes:              Object,
        viewTypeSplits:         Object,
        detailedPositions:      Object,
        componentCodes:         Object,
        componentTags:          Object,
        refrigerants:           Object,
        damageTags:             Object,
        repairCodes:            Object,
        repairTags:             Object,
        stsCodes:               Object,

        surveyTypeMachinery:    String,
    },
    data: function () {
        return {
            replacementData: [],
            loading: false,
            // Pagination
            paginationVisibleButtons: 7,
            currentPage:              1,
            pageSize:                 50,
            // Sorting
            ownSortingColumn:    this.sortingColumn,
            ownSortingDirection: this.sortingDirection,

            icon: 'mdi-checkbox-blank-outline',
            selectValues: {},
            removedRows:  [],

            activeFilterValues: {},
            activeFilter:       false,

            selectedRowIndex: false,
            editRowIndex:     false,
        }
    },
    watch: {
        data: function () {
            this.$forceUpdate();
        },
        currentPage: function() {
            // reset selected row on pagination change
            this.selectedRowIndex = false;
        },
        sortingColumn: function () {
            this.ownSortingColumn = this.sortingColumn;
        },
        sortingDirection: function () {
            this.ownSortingDirection = this.sortingDirection;
        }
    },
    methods: {
        setScrollTop() {
            this.$refs.replacementTable.scrollTo(0, 0)
        },
        setPaginationChange(pageSize) {
            this.selectedRowIndex = false;
            this.pageSize = pageSize;
            if (this.currentPage > this.pageCount) {
                this.currentPage = this.pageCount;
            }
        },
        changeSorting(columnId) {
            // every tree column is sorted ascending by default
            // so change sorting of one of these columns is only possible to descending
            // revert the descending sorting means the column is sorted ascending again
            // all columns after inspection criteria can be sorted ascending or
            let sortDirections = ['asc', 'desc', ''];
            let nextSortIndex = 0;
            if (this.ownSortingColumn === columnId) {
                nextSortIndex = sortDirections.indexOf(this.ownSortingDirection);
                nextSortIndex++;
                if (nextSortIndex >= sortDirections.length) {
                    nextSortIndex = 0;
                }
            }
            this.ownSortingDirection = sortDirections[nextSortIndex];
            if (this.ownSortingDirection === '') {
                this.ownSortingColumn = null;
            } else {
                this.ownSortingColumn = columnId;
            }

            this.$emit('updateView', 'sortingColumn', this.ownSortingColumn, this.ownSortingDirection);
        },
        showFilter(id) {
            this.$refs.listFilters.activeFilter = id;
        },
        onFiltersChange(filterData, showResultGroup) {
            this.activeFilterValues = filterData;
            this.$emit('filterData', filterData, showResultGroup);
        },
        getRowSpanData(column, index) {
            let currentIndex = index;
            let rowSpan;
            while (currentIndex >= 0) {
                if (typeof this.data[currentIndex]['rowSpan_' + column] !== 'undefined') {
                    rowSpan = this.data[currentIndex]['rowSpan_' + column];
                    break;
                }
                currentIndex--;
            }

            return {
                rowSpan: rowSpan,
                index: currentIndex
            }
        },
        openPopup(type) {
            alert('open Popup for ' + type);
        },
        showLoading() {
            this.loading = true;
        },
        hideLoading() {
            this.loading = false;
        },
        selectRow(index) {
            this.selectedRowIndex = index - this.paginationOffset;
        },
        unselectRow() {
            this.selectedRowIndex = false;
        },
        addRow() {
            this.$emit('addRow');
        },
        filterTitle(columnId) {
            // check available filters
            if (
                !this.activeFilterValues[columnId]
                || !this.activeFilterValues[columnId].length
            ) {
                return '';
            }
            let filterData = false;
            this.filters.forEach(iteratingFilter => {
                if (iteratingFilter.name === columnId) {
                    filterData = iteratingFilter.data;
                }
            });
            if (filterData === false) {
                return '';
            }
            // extract filter names
            let texts = [];
            filterData.forEach(filter => {
                if (this.activeFilterValues[columnId].includes(filter.id)) {
                    texts.push(filter.name);
                }
            });
            // join names with max 10 results and a '+ x more' option
            let output;
            if (texts.length > 10) {
                output = texts.slice(0, 10).join('\r\n') + '\r\n+ ' + (texts.length - 10) + ' ' + i18n.t('more');
            } else {
                output = texts.join('\r\n');
            }
            return output;
        },
        getStsCodeName(position, mode, code1, code2, code3, code4)
        {
            position = parseInt(position)
            if (
                position < 1
                || position > 4
                || typeof this.stsCodes[position] === 'undefined'
                || typeof this.stsCodes[position][mode] === 'undefined'
                || typeof this.stsCodes[position][mode][code1] === 'undefined'
            ) {
                return '';
            }

            if (parseInt(position) === 1) {
                return this.stsCodes[position][mode][code1]
            }
            if (typeof this.stsCodes[position][mode][code1][code2] === 'undefined') {
                return '';
            }

            if (parseInt(position) === 2) {
                return this.stsCodes[position][mode][code1][code2]
            }
            if (typeof this.stsCodes[position][mode][code1][code2][code3] === 'undefined') {
                return '';
            }

            if (parseInt(position) === 3) {
                return this.stsCodes[position][mode][code1][code2][code3]
            }
            if (typeof this.stsCodes[position][mode][code1][code2][code3][code4] === 'undefined') {
                return '';
            }

            return this.stsCodes[position][mode][code1][code2][code3][code4];
        },
        stsCodeTitle(rowData) {
            let title = ['STS Code Details:'];
            let mode = this.modes[rowData.surveyType];

            title.push('Code 1: ', this.getStsCodeName(1, mode, rowData.stsCode1));
            title.push('Code 2: ', this.getStsCodeName(2, mode, rowData.stsCode1, rowData.stsCode2));
            title.push('Code 3: ', this.getStsCodeName(3, mode, rowData.stsCode1, rowData.stsCode2, rowData.stsCode3));
            title.push('Code 4: ', this.getStsCodeName(4, mode, rowData.stsCode1, rowData.stsCode2, rowData.stsCode3, rowData.stsCode4));

            return title.join('\n');
        },
        handleRowClick(index, doubleClick) {
            // if row is not selected, select it
            this.selectedRowIndex = index;
            if (doubleClick) {
                // else open editor
                this.$emit('editRow', this.paginationOffset + this.selectedRowIndex);
            } else {
                // else open editor
                this.$emit('selectRow', this.paginationOffset + this.selectedRowIndex);
            }
        },
        viewTypeDisplay(rowData) {
            let split = this.viewTypeSplits[rowData.viewType];
            if (split) {
                return split[rowData.positionType];
            } else {
                return this.viewTypes[rowData.viewType];
            }
        },
        modeDisplay(item)
        {
            return replacementTrait.modeDisplay(item, this.equipment, this.modes, this.modeReplacements);
        }
    },
    computed: {
        visibleColumns() {
            let visibleColumns = [];
            for (let columnIndex in this.columns) {

                let column  = this.columns[columnIndex];
                // column is visible if visible value is set
                // further
                // - survey type, view type and detailed position are only visible, if equipment is not machinery
                // - service type and machinery manufacturer is only visible, if equipment is machinery
                let nonMachineryColumns = ['surveyType', 'viewType', 'detailedPosition', 'width', 'perimeter'];
                let machineryColumns    = ['serviceType', 'machineryManufacturer', 'machineryType', 'refrigerant'];
                visibleColumns[column.id] =
                    column.visible
                    && (
                        (
                            !nonMachineryColumns.includes(column.id)
                            && !machineryColumns.includes(column.id)
                        ) || (
                            nonMachineryColumns.includes(column.id)
                            && !this.isMachinery
                        ) || (
                            machineryColumns.includes(column.id)
                            && this.isMachinery
                        )
                    );
            }
            return visibleColumns;
        },
        columnOrder() {
            let columnOrder = [];
            // isoCode - inspectionCriteria
            for (let columnIndex in this.columns) {
                if (this.layoutType !== this.columns[columnIndex].id) {
                    columnOrder.push(this.columns[columnIndex].id);
                    if (this.columns[columnIndex].id === 'inspectionCriteria') {
                        break;
                    }
                }
            }
            return columnOrder;
        },
        pageCount() {
            if (!this.data) {
                return 0;
            }
            return Math.ceil(this.data.length / this.pageSize);
        },
        paginationOffset() {
            return (this.currentPage - 1) * this.pageSize;
        },
        paginatedData() {
            if (!this.data || this.data.length === 0) {
                return [];
            }
            let start = (this.currentPage - 1) * this.pageSize;
            let end = Math.min(this.currentPage * this.pageSize, this.data.length);

            let paginatedData = [];
            for (let i = start; i < end; i++) {
                paginatedData.push(Object.assign({}, this.data[i]));
            }

            return paginatedData;
        },
        filters() {
            let filters = [];

            for (let key in this.columns) {
                let column = this.columns[key];
                if (!column.filter) {
                    continue;
                }
                if (typeof column.filter !== 'string') {
                    column.filter = 'data'; // default
                }

                let filter = {
                    name:  column.id,
                    title: column.title,
                    type:  column.filter
                };
                let data = this[column.id + 's'];
                if (data) {
                    if (column.id === 'mode') {
                        let newData = [];
                        for (let index in data) {
                            newData[data[index]] = data[index];
                        }
                        data = newData;
                    }
                    let showIdOrCodeInName = ['componentCode', 'repairCode'].includes(column.id);
                    data = this.$convertObjectToObjectsInArray(data, 'id', 'name', showIdOrCodeInName);

                    filter.data = data;
                }

                filters.push(filter);
            }

            return filters;
        },

        isMachinery() {
            return this.equipment === equipments.MACHINERY;
        },

        containerHeightsShort() {
            let short = [];
            Object.keys(this.containerHeights).forEach((key) => {
                let value = this.containerHeights[key];
                short[key] = value.replace(' heights', '').replace('Height ', '');
            });

            return short;
        }
    }
}
</script>

<style scoped>
    div.editor-table.replacements {
        background-image: url('../../../assets/background_replacements.svg');
    }
    table.vcs-table th.vcs-table-header {
        font-size: var(--font-size-table-header);
    }
    table.vcs-table td {
        font-size: var(--font-size-table-data);
    }
    td.stsCode {
        font-weight: bold;
        white-space: nowrap;
    }
</style>